import { ApiClient } from '../utils/apiClient';
import { secureTokenStorage } from '../utils/secureTokenStorage';
import {
  clearAdminSession,
  isAdminSession,
  setAdminSession,
  type AdminUser,
} from '../constants/adminSession';
import { logger } from '../utils/logger';
import type { ApiResponse } from './authService';

export interface AdminLoginRequest {
  username: string;
  password: string;
}

export interface AdminLoginResponse {
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number;
  user: AdminUser;
}

export interface AdminMeResponse extends AdminUser {
  lastLoginAt?: string;
}

class AdminAuthService {
  async login(credentials: AdminLoginRequest): Promise<AdminLoginResponse> {
    try {
      const response = await ApiClient.post<ApiResponse<AdminLoginResponse>>(
        '/admin/login',
        credentials,
        { skipAuth: true, skip401Handler: true },
      );

      if (!response.success) {
        throw new Error(response.message || '登录失败');
      }

      return this.persistAdminLogin(response.data);
    } catch (error) {
      logger.error('Admin login error:', error);
      throw new Error(this.getFriendlyErrorMessage(error));
    }
  }

  async fetchCurrentAdmin(): Promise<AdminMeResponse> {
    const response = await ApiClient.get<ApiResponse<AdminMeResponse>>('/admin/me');
    if (!response.success) {
      throw new Error(response.message || '获取管理员信息失败');
    }
    setAdminSession(response.data.username);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      if (secureTokenStorage.hasAccessToken() && isAdminSession()) {
        await ApiClient.post<ApiResponse<null>>('/admin/logout', {});
      }
    } catch (error) {
      logger.warn('Admin logout request failed:', error);
    } finally {
      clearAdminSession();
      secureTokenStorage.clearTokens();
      window.dispatchEvent(new CustomEvent('admin-logout'));
    }
  }

  isAuthenticated(): boolean {
    return isAdminSession() && secureTokenStorage.hasAccessToken();
  }

  private persistAdminLogin(data: AdminLoginResponse): AdminLoginResponse {
    localStorage.removeItem('user');
    setAdminSession(data.user.username);
    secureTokenStorage.setAccessToken(data.accessToken, data.expiresIn);
    try {
      sessionStorage.removeItem('wavelink_logged_out');
    } catch {
      // ignore
    }
    window.dispatchEvent(new CustomEvent('admin-login-success'));
    return data;
  }

  private getFriendlyErrorMessage(error: unknown): string {
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('fetch') || errorMessage.includes('Network')) {
      return '网络连接失败，请检查网络设置后重试';
    }
    if (errorMessage.includes('401') || errorMessage.includes('密码')) {
      return '用户名或密码错误';
    }
    if (errorMessage.includes('429')) {
      return '登录尝试过于频繁，请稍后再试';
    }
    return errorMessage || '登录失败，请稍后重试';
  }
}

export const adminAuthService = new AdminAuthService();
